import type { Game } from "@engine/core/Game";
import type { Entity } from "@engine/ecs/components";

type EntityProvider = () => Iterable<Entity>;
type Listener = (entity: Entity | null) => void;

const PICK_RADIUS = 32;

/**
 * Click-to-select entity inspector.
 * Alt+click on the canvas picks the entity nearest the click point (within
 * 32px). Escape deselects. Subscribers (React HUD) get notified.
 */
export class Inspector {
  private provider: EntityProvider = () => [];
  private selected: Entity | null = null;
  private listeners = new Set<Listener>();
  private detach: (() => void) | undefined;

  constructor(private readonly game: Game) {}

  setEntityProvider(p: EntityProvider): void {
    this.provider = p;
  }

  clearEntityProvider(): void {
    this.provider = () => [];
    this.select(null);
  }

  attach(): void {
    if (this.detach) return;
    const canvas = this.game.app.canvas;
    const onClick = (ev: MouseEvent): void => {
      if (!ev.altKey) return;
      ev.preventDefault();
      // Stage coords are CSS-pixel based when autoDensity is on, so we use
      // bounding-rect offsets directly (no DPR scaling).
      const rect = canvas.getBoundingClientRect();
      const sx = ev.clientX - rect.left;
      const sy = ev.clientY - rect.top;
      const world = this.game.camera.screenToWorld({ x: sx, y: sy });
      const picked = this.findClosest(world.x, world.y);
      this.select(picked);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.code === "Escape" && this.selected) this.select(null);
    };
    canvas.addEventListener("click", onClick);
    window.addEventListener("keydown", onKey);
    this.detach = () => {
      canvas.removeEventListener("click", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }

  detachListeners(): void {
    this.detach?.();
    this.detach = undefined;
  }

  select(e: Entity | null): void {
    if (this.selected === e) return;
    this.selected = e;
    for (const fn of this.listeners) fn(e);
  }

  current(): Entity | null {
    return this.selected;
  }

  /** Find the nearest entity to (wx, wy) within `radius` px (default 32). */
  pick(wx: number, wy: number, radius = PICK_RADIUS): Entity | null {
    let best: Entity | null = null;
    let bestDist = radius * radius;
    for (const e of this.provider()) {
      if (!e.position) continue;
      const dx = e.position.x - wx;
      const dy = e.position.y - wy;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestDist) {
        bestDist = d2;
        best = e;
      }
    }
    return best;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  destroy(): void {
    this.detachListeners();
    this.listeners.clear();
    this.selected = null;
  }

  private findClosest(wx: number, wy: number): Entity | null {
    return this.pick(wx, wy);
  }
}
